import React, { useState } from 'react';
import { Item, Board } from '@/stores/canvasStore';
import { Pin, X, FileText, StickyNote, Film, File } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { isTweetItem } from '@/components/items/TweetPreviewCard';
import { TweetMiniPreview } from '@/components/items/TweetMiniPreview';
import { getLinkResultTitle } from '@/lib/searchResultPreview';
import { SocialMediaCard } from '@/components/items/SocialMediaCard';
import { ScrapedCreativeCard } from '@/components/items/ScrapedCreativeCard';
import { isImageFirstSocial, type SocialCardContent } from '@/lib/socialCard';
import { getStickyColorValue } from '@/lib/colorUtils';

/** Pinned link tile: image if it loads, else just the title. Never prints alt text. */
function PinnedLinkPreview({ item }: { item: Item }) {
  const [imageError, setImageError] = useState(false);
  const image = (item.content as Record<string, unknown>)?.image;
  return (
    <div className="w-full h-full bg-paper-raised flex flex-col">
      {typeof image === 'string' && image && !imageError && (
        <img src={image} alt="" className="w-full h-2/3 object-cover" onError={() => setImageError(true)} />
      )}
      <div className="p-2 min-h-0">
        <p className="text-xs text-ink line-clamp-2">{getLinkResultTitle(item)}</p>
      </div>
    </div>
  );
}

interface PinnedSectionProps {
  pinnedItems: Item[];
  boards: Board[];
  onUnpin: (itemId: string) => void;
  onItemClick: (item: Item) => void;
}

export function PinnedSection({ pinnedItems, boards, onUnpin, onItemClick }: PinnedSectionProps) {
  const isMobile = useIsMobile();
  
  if (pinnedItems.length === 0) return null;

  const getBoardForItem = (item: Item): Board | undefined => {
    return boards.find(b => b.id === item.board_id);
  };

  // Helper to get board label - shows "Home Board" for root board
  const getBoardLabel = (item: Item, board?: Board): { label: string; color: string | null } => {
    if (!item.board_id) {
      return { label: 'Not in any board', color: null };
    }
    
    if (!board) {
      return { label: 'Not in any board', color: null };
    }
    
    const isHomeBoard = board.name === 'Home' || board.parent_id === null;
    
    return {
      label: isHomeBoard ? 'Home Board' : board.name,
      color: board.icon_color || '#6366F1'
    };
  };

  const renderMiniPreview = (item: Item) => {
    const content = item.content as Record<string, unknown>;

    switch (item.item_type) {
      case 'image':
        return (
          <img
            src={content.url as string}
            alt={content.alt as string || 'Image'}
            className="w-full h-full object-cover"
          />
        );

      case 'sticky':
        const color = (content.color as string) || 'yellow';
        const bgColor = getStickyColorValue(color);
        return (
          <div
            className="w-full h-full p-3 flex items-start"
            style={{ backgroundColor: bgColor }}
          >
            <p 
              className="text-xs leading-relaxed line-clamp-3"
              style={{ color: '#17181A' }}
            >
              {content.text as string || 'Empty note'}
            </p>
          </div>
        );

      case 'text_card':
        return (
          <div className="w-full h-full p-3 bg-paper-raised">
            <p className="text-xs text-ink-2 leading-relaxed line-clamp-4">
              {(content.body as string || '').slice(0, 100)}
            </p>
          </div>
        );

      case 'document':
        const pinnedDocBody = (content.body as string) || (content.content as string) || '';
        const pinnedDocText = pinnedDocBody.replace(/<[^>]*>/g, '');
        return (
          <div className="w-full h-full p-3 bg-paper-raised flex flex-col">
            <div className="flex items-center gap-1.5 mb-1">
              <FileText className="w-3 h-3 text-primary" />
              <span className="text-xs font-medium text-ink truncate">
                {content.title as string || 'Document'}
              </span>
            </div>
            <p className="text-xs text-ink-3 line-clamp-2">
              {pinnedDocText.slice(0, 60)}
            </p>
          </div>
        );

      case 'scraped_creative':
        return <ScrapedCreativeCard item={item} framed={false} autoRefresh={false} compact />;

      case 'link':
        if (isTweetItem(item)) {
          return <TweetMiniPreview item={item} />;
        }
        // Instagram/TikTok: the same card as the canvas. The main Discover tile does the refreshing.
        if (isImageFirstSocial(content as SocialCardContent)) {
          return <SocialMediaCard item={item} framed={false} openOnClick={false} autoRefresh={false} compact />;
        }
        return <PinnedLinkPreview item={item} />;

      case 'video':
        return (
          <div className="w-full h-full bg-paper-sunken flex items-center justify-center">
            {content.thumbnailUrl ? (
              <img 
                src={content.thumbnailUrl as string} 
                alt="Video"
                className="w-full h-full object-cover"
              />
            ) : (
              <Film className="w-6 h-6 text-ink-3" />
            )}
          </div>
        );

      case 'file':
        return (
          <div className="w-full h-full p-3 bg-paper-sunken flex items-center gap-2">
            <File className="w-4 h-4 text-ink-3" />
            <span className="text-xs text-ink truncate">
              {content.filename as string}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mb-6 mx-4 md:mx-6 lg:mx-8 bg-paper-sunken py-6 rounded-3xl">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Pin className="w-4 h-4 text-ink-2" />
          <h2 className="text-sm font-semibold text-ink-3 uppercase tracking-wider">
            Pinned
          </h2>
          <span className="text-xs text-ink-3">
            ({pinnedItems.length})
          </span>
        </div>
        
        {/* Outer container with overflow visible for buttons */}
        <div className="relative" style={{ overflow: 'visible' }}>
          {/* Scroll container with padding for X button overflow */}
          <div 
            className="flex gap-5 overflow-x-auto pb-4 pt-4 px-4 -mx-4 scrollbar-hide scroll-smooth"
            style={{ overflow: 'auto visible' }}
          >
            {pinnedItems.map(item => {
              const board = getBoardForItem(item);
              
              return (
                <div
                  key={item.id}
                  className="relative flex-shrink-0 w-56 group cursor-pointer"
                  style={{ overflow: 'visible' }}
                  onClick={() => onItemClick(item)}
                >
                  {/* Unpin button - positioned OUTSIDE, must be before card for z-index */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnpin(item.id);
                    }}
                    className="
                      absolute -top-3 -right-3 z-20
                      p-1.5 
                      bg-paper-raised hover:bg-danger
                      border border-line
                      rounded-full
                      opacity-0 group-hover:opacity-100
                      transition-all duration-200
                      shadow-lg
                      hover:text-danger-foreground
                    "
                    title="Unpin"
                  >
                    <X className="w-3 h-3 text-ink" />
                  </button>
                  
                  {/* Mini card preview with 3:2 aspect ratio */}
                  <div 
                    className="rounded-xl overflow-hidden bg-paper-raised shadow-sm hover:shadow-md transition-shadow"
                    style={{ aspectRatio: '3/2' }}
                  >
                    {renderMiniPreview(item)}
                  </div>
                  
                  {/* Board indicator - always show */}
                  {(() => {
                    const { label, color } = getBoardLabel(item, board);
                    return (
                      <div className="mt-2 flex items-center gap-1.5">
                        {color ? (
                          <>
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-xs text-ink-3 truncate">
                              {label}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-ink-3 italic truncate">
                            {label}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
            
            {/* Spacer at end */}
            <div className="flex-shrink-0 w-4" />
          </div>
          
          {/* Scroll fade indicators - softened, hidden on mobile */}
          {!isMobile && (
            <>
              <div className="absolute left-0 top-4 bottom-4 w-6 bg-gradient-to-r from-paper-sunken to-transparent pointer-events-none" />
              <div className="absolute right-0 top-4 bottom-4 w-6 bg-gradient-to-l from-paper-sunken to-transparent pointer-events-none" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
